import { create } from 'zustand';
import type { TransferFailure } from '@/lib/calls/transfer.types';

/**
 * The one transfer outcome the agent cannot act on.
 *
 * TRANSFER_FAILED means the transfer did NOT happen and the agent's leg is
 * already gone, with the caller dropped into voicemail. The in-call HUD unmounts
 * the instant that leg dies, so the notice cannot live there — and a toast would
 * slide away while the agent is still working out where their customer went.
 *
 * It is held here instead, rendered by a banner mounted alongside the HUD rather
 * than inside it, and cleared only by hand.
 */
interface TransferFailureState {
  lostCall: TransferFailure | null;
  reportCallLost: (failure: TransferFailure) => void;
  dismissCallLost: () => void;
}

/**
 * The ask-first transfer this browser is running, if any.
 *
 * Held here rather than in the transfer popover: the agent closes the popover to
 * talk, and the transfer has to stay visible and finishable in the call bar the
 * whole time — through the ring, the conversation with the teammate, and the
 * hand-over or the return to the caller.
 */
export interface WarmTransfer {
  /** The CALLER's leg — what every transfer request names. */
  callControlId: string;
  /** The voip store's entry for this call, for muting and unmuting it. */
  callId: string;
  targetName: string;
  /** When it started — tells one transfer on a call from the next. */
  startedAt: number;
  /**
   * True when the transfer muted the agent (so the caller hears only the hold
   * audio while the teammate rings). Only then is it unmuted again: a mute the
   * agent chose themselves is theirs to lift.
   */
  mutedByTransfer: boolean;
}

/**
 * What the agent is told after a transfer that left them with the caller —
 * kept with the call it is about. With two calls up, "you're back with the
 * caller" drawn under the OTHER one would name the wrong customer.
 */
export interface WarmTransferNotice {
  callId: string;
  message: string;
}

interface WarmTransferStoreState {
  warm: WarmTransfer | null;
  notice: WarmTransferNotice | null;
  begin: (warm: WarmTransfer) => void;
  update: (patch: Partial<WarmTransfer>) => void;
  end: (notice?: string | null) => void;
  dismissNotice: () => void;
}

export const useWarmTransferStore = create<WarmTransferStoreState>((set) => ({
  warm: null,
  notice: null,
  begin: (warm) => set({ warm, notice: null }),
  update: (patch) => set((s) => (s.warm ? { warm: { ...s.warm, ...patch } } : s)),
  end: (message = null) =>
    set((s) => ({
      warm: null,
      notice: message && s.warm ? { callId: s.warm.callId, message } : null,
    })),
  dismissNotice: () => set({ notice: null }),
}));

export const useTransferFailureStore = create<TransferFailureState>((set) => ({
  lostCall: null,
  reportCallLost: (failure) => set({ lostCall: failure }),
  dismissCallLost: () => set({ lostCall: null }),
}));
